import { z } from 'zod';
import { type Country } from 'react-phone-number-input';
import {
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from 'libphonenumber-js';

const MIN_PHONE_DIGITS = 7;
const PT_REQUIRED_DIGITS = 9;
const PT_VALID_PREFIXES = ['9', '2', '3'];

// One owner for the password policy. Every form that sets a password composes
// on this; only the field names differ per form.
export const passwordField = z
  .string()
  .min(6, 'Password must be at least 6 characters long')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[\W_]/, 'Password must contain at least one special character');

// The other half of the policy. Only the wording is shared: each form names its
// pair differently, and the account screen adds a conditional current-password
// field on top, so a generic refine wrapper would have to erase the field types
// it is applied to. Two lines per form beats losing them.
export const PASSWORD_MISMATCH_MESSAGE = 'Passwords do not match';

export const otpSchema = z.object({
  otp: z
    .string()
    .length(6, { message: 'Please enter the 6-digit code' })
    .regex(/^\d{6}$/, { message: 'Code must be 6 digits' }),
});

export const signupSchema = z
  .object({
    first_name: z.string().min(1, { message: 'Please enter first name' }),
    last_name: z.string().min(1, { message: 'Please enter last name' }),
    email: z
      .string()
      .min(1, { message: 'Please enter email' })
      .email({ message: 'Invalid email address' }),
    password: passwordField,
    repeatPassword: z.string(),
    phone: z.string().min(1, { message: 'Please enter your phone number' }),
    country_code: z.string().min(1, { message: 'Please select a country' }),
  })
  // Its own refine, not withPasswordMatch: signup names the pair differently.
  .refine((data) => data.password === data.repeatPassword, {
    message: PASSWORD_MISMATCH_MESSAGE,
    path: ['repeatPassword'],
  })
  .superRefine((data, ctx) => {
    if (!data.phone || !data.country_code) return;

    const digits = data.phone.replace(/\D/g, '');

    if (digits.length > 0 && digits.length < MIN_PHONE_DIGITS) {
      ctx.addIssue({
        code: 'custom',
        path: ['phone'],
        message: `Phone number must have at least ${MIN_PHONE_DIGITS} digits`,
      });
      return;
    }

    if (data.country_code === 'PT' && digits.length > 0) {
      if (digits.length !== PT_REQUIRED_DIGITS) {
        ctx.addIssue({
          code: 'custom',
          path: ['phone'],
          message: `Portuguese numbers must have exactly ${PT_REQUIRED_DIGITS} digits`,
        });
        return;
      }
      if (!PT_VALID_PREFIXES.includes(digits[0])) {
        ctx.addIssue({
          code: 'custom',
          path: ['phone'],
          message: `Portuguese numbers must start with ${PT_VALID_PREFIXES.join(', ')}`,
        });
        return;
      }
    }

    try {
      const reason = validatePhoneNumberLength(
        data.phone,
        data.country_code as Country,
      );

      if (reason) {
        const messageMap: Record<string, string> = {
          TOO_SHORT: 'Phone number is too short for the selected country',
          TOO_LONG: 'Phone number is too long for the selected country',
          INVALID_LENGTH:
            'Phone number length is invalid for the selected country',
        };
        ctx.addIssue({
          code: 'custom',
          path: ['phone'],
          message: messageMap[reason] || 'Invalid phone number',
        });
        return;
      }

      const phoneNumber = parsePhoneNumberFromString(
        data.phone,
        data.country_code as Country,
      );
      if (!phoneNumber || !phoneNumber.isValid()) {
        ctx.addIssue({
          code: 'custom',
          path: ['phone'],
          message: 'Invalid phone number',
        });
        return;
      }
    } catch {
      ctx.addIssue({
        code: 'custom',
        path: ['phone'],
        message: 'Invalid phone number',
      });
    }
  });

// The backend's rule: zod's email(), which every order create schema runs,
// capped at 254 as its guest schema is. It refuses some addresses the
// sentences below let through ("sofia@gmail.c"), and a refused body is a 422
// that pages ops.
const SERVER_EMAIL = z.string().email().max(254);

// What is wrong with a typed, non-empty address, or null when nothing is.
export const emailFormatError = (email: string): string | null => {
  if (!email.includes('@')) {
    return "You missed the '@' symbol. An email should look like 'you@example.com'.";
  }
  if (email.startsWith('@')) {
    return "It looks like the first part is missing. Don't forget your username before the '@'.";
  }
  const domain = email.slice(email.indexOf('@') + 1);
  if (!domain || !domain.includes('.')) {
    return "It looks like the email is incomplete. Did you forget the '.com'?";
  }
  if (!SERVER_EMAIL.safeParse(email).success) {
    return "That doesn't look like a valid email";
  }
  return null;
};

export const emailSchema = z
  .object({
    email: z
      .string()
      .min(1, { message: 'Enter your email address' })
      .transform((v) => v.trim().toLowerCase()),
  })
  .superRefine((data, ctx) => {
    const error = data.email ? emailFormatError(data.email) : null;
    if (error)
      ctx.addIssue({ code: 'custom', path: ['email'], message: error });
  });

export type EmailFormValues = z.infer<typeof emailSchema>;
export type SignupFormValues = z.infer<typeof signupSchema>;
export type OtpFormValues = z.infer<typeof otpSchema>;
